import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function RequirementsForm() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState({});
  const [status, setStatus] = useState("draft");
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    api.get(`/projects/${orderId}/requirement`).then(r => {
      setSchema(r.data.schema);
      const req = r.data.requirement;
      if (req) {
        setData(req.data || {});
        setProgress(req.progress || 0);
        setStatus(req.status);
      }
    });
  }, [orderId]);

  // autosave with debounce
  useEffect(() => {
    if (!schema || status === "submitted") return;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await api.put(`/projects/${orderId}/requirement`, { data });
        setProgress(r.data.progress);
      } catch {} finally { setSaving(false); }
    }, 1000);
    return () => clearTimeout(t);
  }, [data, schema, status, orderId]);

  const setField = (k, v) => setData(d => ({ ...d, [k]: v }));

  const uploadFiles = async (key, files) => {
    setUploadingKey(key);
    try {
      const current = data[key] || [];
      // Upload into the project's own Files system (References folder) — not the
      // generic reference-image bucket — so the designer/admin Files tab actually
      // shows what the client attached instead of it being invisible to them.
      const uploads = await Promise.all(
        Array.from(files).slice(0, 5 - current.length).map(f => {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("folder", "References");
          fd.append("category", "reference");
          return api.post(`/projects/${orderId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        })
      );
      setField(key, [...current, ...uploads.map(u => u.data.id)]);
      toast.success(`${uploads.length} image(s) uploaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const removeFile = (key, idx) => setField(key, (data[key] || []).filter((_, i) => i !== idx));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/projects/${orderId}/requirement/submit`);
      toast.success("Requirements submitted! Our team will get to work.");
      nav(`/dashboard/orders/${orderId}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.missing) toast.error("Please fill required fields: " + detail.missing.join(", "));
      else toast.error(formatApiError(detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!schema) return <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-10 text-center text-gray-500">Loading…</div>;

  return (
    <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 sm:p-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Project Requirements</h2>
          <p className="text-sm text-gray-500 mt-1">The more detail you share, the better the design.</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500 uppercase font-semibold">Completion</div>
          <div className="text-2xl font-bold text-[#F7941D]">{progress}%</div>
        </div>
      </div>

      <div className="mt-4 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-[#F7941D] to-[#e0861a] transition-all" style={{ width: `${progress}%` }} />
      </div>
      {saving && <div className="text-xs text-gray-500 mt-2 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}

      {status === "submitted" ? (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm font-semibold">
          Requirements submitted — our team is on it. You can't edit this anymore, but you can see what you told us below.
        </div>
      ) : null}

      <div className="mt-6 space-y-6">
        {schema.sections.map(sec => (
          <div key={sec.key}>
            <h3 className="text-base font-bold text-[#1a3c5e] mb-3">{sec.label}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {sec.fields.map(f => (
                <CustField key={f.key} f={f} value={data[f.key]} disabled={status === "submitted"}
                  onChange={v => setField(f.key, v)}
                  onUpload={files => uploadFiles(f.key, files)}
                  onRemoveFile={idx => removeFile(f.key, idx)}
                  uploading={uploadingKey === f.key} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {status !== "submitted" && (
        <button onClick={submit} disabled={submitting || progress < 100} data-testid="req-submit"
          className="mt-8 w-full sm:w-auto px-8 py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Requirements
        </button>
      )}
    </div>
  );
}

function CustField({ f, value, onChange, disabled, onUpload, onRemoveFile, uploading }) {
  const base = "w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D] disabled:bg-gray-50 disabled:text-gray-500";
  const label = (
    <label className="text-xs font-semibold text-[#1a3c5e] uppercase block mb-1">
      {f.label}{f.required && <span className="text-rose-500 ml-1">*</span>}
      {f.help && <span className="text-gray-400 normal-case font-normal ml-1 italic">— {f.help}</span>}
    </label>
  );

  if (f.type === "files") {
    const files = value || [];
    return (
      <div className="sm:col-span-2" data-testid={`req-${f.key}`}>
        {label}
        {!disabled && (
          <label className={`block border-2 border-dashed border-[#F7941D]/40 rounded-xl py-6 text-center cursor-pointer hover:bg-[#FEF3E2]/40 transition ${files.length >= 5 ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload className="w-5 h-5 mx-auto text-[#F7941D] mb-1.5" />
            <div className="text-sm text-[#1a3c5e] font-semibold">{uploading ? "Uploading…" : "Click to upload images"}</div>
            <div className="text-xs text-gray-500 mt-1">JPG / PNG / WEBP, up to 5</div>
            <input type="file" accept="image/*" multiple hidden disabled={uploading || files.length >= 5} onChange={e => e.target.files.length && onUpload(e.target.files)} />
          </label>
        )}
        {files.length > 0 && (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {files.map((id, i) => (
              <div key={id} className="relative aspect-square rounded-lg overflow-hidden bg-[#FEF3E2] border border-[#F7941D]/20">
                <img src={`${api.defaults.baseURL}/project-files/${id}/download`} alt="" className="w-full h-full object-cover" />
                {!disabled && (
                  <button type="button" onClick={() => onRemoveFile(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (f.type === "textarea") return (
    <div className="sm:col-span-2" data-testid={`req-${f.key}`}>{label}
      <textarea rows={4} disabled={disabled} value={value || ""} onChange={e => onChange(e.target.value)} className={base} />
    </div>
  );

  if (f.type === "boolean") return (
    <label className="flex items-center gap-2 text-sm sm:col-span-2" data-testid={`req-${f.key}`}>
      <input type="checkbox" disabled={disabled} checked={!!value} onChange={e => onChange(e.target.checked)} className="w-4 h-4 accent-[#F7941D]" />
      <span className="text-[#1a3c5e] font-semibold">{f.label}</span>
    </label>
  );

  if (f.type === "select") return (
    <div data-testid={`req-${f.key}`}>{label}
      <select disabled={disabled} value={value || ""} onChange={e => onChange(e.target.value)} className={`${base} bg-white`}>
        <option value="">— Select —</option>
        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  if (f.type === "number") return (
    <div data-testid={`req-${f.key}`}>{label}
      <input type="number" disabled={disabled} value={value ?? ""} onChange={e => onChange(e.target.value)} className={base} />
    </div>
  );

  return (
    <div data-testid={`req-${f.key}`}>{label}
      <input type="text" disabled={disabled} value={value || ""} onChange={e => onChange(e.target.value)} className={base} />
    </div>
  );
}
